import React, { useState, useContext, useEffect, useCallback } from 'react';

import {
  useWindowDimensions,
  useDirectTransCheck,
  useIECheck,
  isSchemaValid,
  isCustomValid,
  sortErrors,
  scrollTop,
} from 'utils';
import AppContext from 'fluxes/context';
import { CHOICES_MAP, CHOICES_NAME_MAP } from 'utils/constant';
import { UPDATE_ADD_ENVI_INPUT, CHANGE_VALID } from 'fluxes/actions';

import PresentPC from 'components/pages/AddEnvi/PresentPC';
import PresentSP from 'components/pages/AddEnvi/PresentSP';
import DotSlider from 'components/atoms/Input/DotSlider';

const AddEnvi = () => {
  const path = window.location.pathname;
  const { state, dispatch } = useContext(AppContext);

  // レスポンシブ
  const { width } = useWindowDimensions();
  const isIE = useIECheck();
  const isSp = width <= 640 && !isIE;

  // 状態変数
  const [welfare, setWelfare] = useState(state.welfare);
  const [welfareCmnt, setWelfareCmnt] = useState(state.welfareCmnt);
  const [workEnvi, setWorkEnvi] = useState(state.workEnvi);
  const [ageRange, setAgeRange] = useState(state.ageRange);
  const [genderRange, setGenderRange] = useState(state.genderRange);
  const [workEnviCmnt, setWorkEnviCmnt] = useState(state.workEnviCmnt);

  // 利用可能な福利厚生施設 の特殊値
  const gaitounasiNum = '09';
  const welfareSonotaNum = '05';

  // welfareの「その他」が選択されているかどうかのフラグ
  const isWelfareCmntTextAreaVisible = welfare.includes(welfareSonotaNum);

  // 直接遷移チェック
  useDirectTransCheck({ state });

  //------------------------------
  // 入力チェック ---------->>
  const initialValid = { valid: state.validMap, errorMessages: [] };
  const [valids, setValids] = useState({
    schema: initialValid, // 画面全体のチェック
    checkAgeRangeLessThanZero: initialValid 
  });

  // 初期入力チェック設定
  // 表示したいものだけ
  const [isShowError, setIsShowError] = useState({
    welfareCmnt: false,
    workEnviCmnt: false,
    ageRange: false,
  });

  // エラーの画面表示で利用する値
  const valid = Object.keys(valids).every((key) => valids[key].valid);
  const dispErrors = useCallback(() => {
    const {
      schema,
      checkAgeRangeLessThanZero,
    } = valids;

    const errors = sortErrors([
      ...schema.errorMessages,
      ...checkAgeRangeLessThanZero.errorMessages,    
    ]);
    const errorMap = errors.reduce((obj, error) => {
      const { target, message } = error;
      if (!isShowError[target] || Object.keys(obj).includes(target)) return obj;
      return { ...obj, [target]: message };
    }, {});
    return errorMap;
  }, [isShowError, valids]);

  // 入力チェック（全体）
  useEffect(() => {
    const [isValid, newErrorMessages] = isSchemaValid({
      target: {
        ageRange,
        genderRange,
        welfare,
        welfareCmnt,
        workEnvi,
        workEnviCmnt,
        isWelfareCmntTextAreaVisible,
      },
    });

    // erros更新
    const newValid = {
      schema: { valid: isValid, errorMessages: newErrorMessages },
    };
    setValids((org) => ({ ...org, ...newValid }));
  }, [
    ageRange,
    genderRange,
    welfare,
    welfareCmnt,
    workEnvi,
    workEnviCmnt,
    isWelfareCmntTextAreaVisible,
    valid,
    path,
  ]);
  // 入力チェック <<----------
  // 最大年齢 - 最小年齢 < 0
  useEffect(() => {
    const ageRangeValidation = async () => {
      // エラーチェックを実行
      const [ isValid, newErrorMessages ] = await isCustomValid({
        checks: [ 'checkLessThanZero' ],
        target: { target: 'ageRange', min: ageRange[0], max: ageRange[1] },
      });
      // erros更新
      const newValid = { checkAgeRangeLessThanZero: { valid: isValid, errorMessages: newErrorMessages } };
      setValids(org => ({ ...org, ...newValid }));
    };
    ageRangeValidation();
  }, [ ageRange ]);
  //------------------------------
  // 「その他」のチェックが外れるとテキストエリアの入力は空になる
  useEffect(() => {
    if (!welfare.includes(welfareSonotaNum)) {
      setWelfareCmnt('');
    }
  }, [welfare]);

  //------------------------------
  // ボタン押下 ------------->>
  // state更新処理
  const updateInputState = () => {
    dispatch({
      type: UPDATE_ADD_ENVI_INPUT,
      updAddEnviInput: {
        ageRange,
        genderRange,
        welfare,
        welfareCmnt,
        workEnvi,
        workEnviCmnt,
      },
    });
  };
  // 押下時の処理
  const onClickMainButton = {
    prev: () => {
      scrollTop();
      updateInputState();
      dispatch({
        type: CHANGE_VALID,
        path,
        valid,
      });
    },
    next: () => {
      scrollTop();
      updateInputState();
      dispatch({
        type: CHANGE_VALID,
        path,
        valid,
      });
    },
  };
  // ボタン押下 <<----------

  // onChange ---------->>
  // 利用可能な福利厚生施設 チェックボックス
  const welfareOnChange = (event) => {
    const { value, checked } = event.target;
    setWelfare((prevCheckboxes) => {
      if (value === gaitounasiNum) {
        return checked ? [gaitounasiNum] : [];
      }
      let newSelection = [...prevCheckboxes];
      if (checked) {
        newSelection = newSelection.filter((arg) => arg !== gaitounasiNum);
        newSelection.push(value);
      } else {
        newSelection = newSelection.filter((arg) => arg !== value);
      }
      return newSelection;
    });
  };

  // 福利厚生施設 テキストエリア
  const welfareCmntOnChange = (event) => {
    const val = event.target.value;
    setWelfareCmnt(val);
    // エラー設定
    if (!isShowError.welfareCmnt) {
      setIsShowError({ ...isShowError, welfareCmnt: true });
    }
  };

  // 職場の特徴（複数選択可）
  const workEnviOnChange = (event) => {
    const val = event.target.value;
    const addItem = [...workEnvi, val];
    const removeItem = workEnvi.filter((item) => {
      return item !== val;
    });
    const newWorkEnvi = workEnvi.includes(val) ? removeItem : addItem;
    setWorkEnvi(newWorkEnvi);
  };

  // 職場の年齢層
  //  min
  const ageRangeMinOnChange = (value) => {
    setAgeRange(ageRange.map((list, index) => (index === 0 ? value : list)));
    if(!isShowError.ageRange) {
      setIsShowError({ ...isShowError, ageRange: true });
    }
  };
  //  max
  const ageRangeMaxOnChange = (value) => {
    setAgeRange(ageRange.map((list, index) => (index === 1 ? value : list)));
    if(!isShowError.ageRange) {
      setIsShowError({ ...isShowError, ageRange: true });
    }
  };

  // 男女比率
  const genderRangeOnChange = (event) => {
    const val = event.target.value;
    setGenderRange(val);
  };

  // 上記以外の特徴 テキストエリア
  const workEnviCmntOnChange = (event) => {
    const val = event.target.value;
    setWorkEnviCmnt(val);
    // エラー設定
    if (!isShowError.workEnviCmnt) {
      setIsShowError({ ...isShowError, workEnviCmnt: true });
    }
  };
  // onChange <<----------

  //  画面表示 ---------->>
  // 利用可能な福利厚生施設 チェックボックス
  const isWelfareDisabled = (e) => {
    if (!!welfare && welfare.includes(gaitounasiNum)) {
      return !welfare.includes(e);
    } else {
      return false;
    }
  };
  const welfareDisp = {
    title: '利用可能な福利厚生施設',
    label: '必須',
    type: 'BasicCheckBoxInput',
    list: CHOICES_MAP.welfareList.map((elem) => {
      const infoStr = 'welfare';
      return {
        id: infoStr + elem.value,
        value: elem.value,
        label: elem.label,
        number: isSp ? 2 : 3.1, // 分割数
        onChange: welfareOnChange,
        checked: welfare.includes(elem.value),
        disabled: isWelfareDisabled(elem.value),
      };
    }),
    // 福利厚生施設 テキストエリア
    comment: {
      title: '福利厚生施設を掲載してください',
      visible: isWelfareCmntTextAreaVisible,
      label: '必須',
      questionMark: false,
      popUpTitle: '',
      popUpDetail: '',
      relativePosArrow: isSp ? '25%' : '45%',
      name: 'welfareCmnt',
      textAreaValue: welfareCmnt,
      cmntOnChange: welfareCmntOnChange,
      onInput: welfareCmntOnChange,
      onCompositionEnd: welfareCmntOnChange,
      maxLength: '500',
      placeholder: '',
      rows: '10',
      cols: '125',
      wordCount: '(500文字以下)',
      errMessage: dispErrors().welfareCmnt,
      inValid: !!dispErrors().welfareCmnt,
    },
  };

  // 職場の特徴（複数選択可）
  const workEnviDisp = {
    title: '職場の特徴（複数選択可）',
    label: CHOICES_NAME_MAP.option,
    type: 'CheckBoxInput',
    list: CHOICES_MAP.workEnviList.map((elem) => {
      const infoStr = 'workEnvi';
      return {
        id: infoStr + elem.value,
        value: elem.value,
        label: elem.label,
        onChange: workEnviOnChange,
        checked: workEnvi.includes(elem.value),
      };
    }),
  };

  // 職場の年齢層
  const DROPDOWN_INITIAL_ITEM_MIN = { value: '', label: '20' };
  const DROPDOWN_INITIAL_ITEM_MAX = { value: '', label: '40' };
  const ageRangeDisp = {
    label: '必須',
    min: {
      ageRangeList: [DROPDOWN_INITIAL_ITEM_MIN, ...CHOICES_MAP.ageList],
      value: ageRange[0],
      onChange: ageRangeMinOnChange,
      inValid: !!dispErrors().ageRange
    },
    max: {
      ageRangeList: [DROPDOWN_INITIAL_ITEM_MAX, ...CHOICES_MAP.ageList],
      value: ageRange[1],
      onChange: ageRangeMaxOnChange,
      inValid: !!dispErrors().ageRange
    },
  };

  // 男女比率
  const genderData = CHOICES_MAP.genderRangeList.map((data) => ({
    id: `gender${data.value}`,
    value: data.value,
    label: data.label,
    checked: genderRange.includes(data.value),
  }));

  const genderRangeDisp = {
    title: '男女比率',
    label: '必須',
    list:
      <>
        <div className="">
          <DotSlider
            items={genderData}
            onChange={genderRangeOnChange}
            name="genderRange"
          />
        </div>
      </>,
  };

  // 上記以外の特徴 テキストエリア
  const workEnviCmntDisp = {
    title: '上記以外に職場環境の特徴がある場合は入力してください',
    label: CHOICES_NAME_MAP.option,
    type: 'TextAreaInput',
    id: 'workEnviCmnt',
    name: 'workEnviCmnt',
    list: [
      {
        placeholder: '',
        maxLength: 500,
        value: workEnviCmnt,
        name: 'workEnviCmnt',
        onInput: workEnviCmntOnChange,
        onChange: workEnviCmntOnChange,
        onCompositionEnd: workEnviCmntOnChange,
        wordCount: '(500文字以下)',
        inValid: !!dispErrors().workEnviCmnt,
      },
    ],
    errorDispMessages: dispErrors().workEnviCmnt,
  };
  //  画面表示 <<----------

  //------------------------------
  return isSp ? (
    <PresentSP
      // 次へ戻るボタン
      onClickMainButton={onClickMainButton}
      valid={valid}
      errorDispMap={dispErrors()}
      // 入力項目
      welfareDisp={welfareDisp}
      workEnviDisp={workEnviDisp}
      ageRangeDisp={ageRangeDisp}
      genderRangeDisp={genderRangeDisp}
      workEnviCmntDisp={workEnviCmntDisp}
    />
  ) : (
    <PresentPC
      // 次へ戻るボタン
      onClickMainButton={onClickMainButton}
      valid={valid}
      errorDispMap={dispErrors()}
      // 入力項目
      welfareDisp={welfareDisp}
      workEnviDisp={workEnviDisp}
      ageRangeDisp={ageRangeDisp}
      genderRangeDisp={genderRangeDisp}
      workEnviCmntDisp={workEnviCmntDisp}
    />
  );
};

export default AddEnvi;
